<template>
	<tr>
		<td>
			<div class="d-flex gap-2 align-items-center">
				<i class="bi bi-three-dots-vertical drag-handle" />

				<div class="btn-group btn-group-sm">
					<!-- play/pause button -->
					<button
						class="btn btn-outline-secondary"
						@click="toggleStatus"
						:title="running ? 'Pause' : 'Continue'"
					>
						<i class="bi bi-pause-fill" v-if="running" />
						<i class="bi bi-play-fill" v-else />
					</button>

					<!-- show/hide button -->
					<button
						class="btn btn-outline-secondary"
						@click="toggleVisibility"
						:title="visible ? 'Hide' : 'Show'"
					>
						<i class="bi bi-eye" v-if="visible" />
						<i class="bi bi-eye-slash" v-else />
					</button>

					<!-- randomize button -->
					<input
						type="checkbox"
						class="btn-check"
						:checked="stats.randomize"
						@click="toggleRandomizeSitemap()"
						:id="randomizeCheckboxId"
					/>
					<label
						class="btn btn-outline-secondary bi bi-shuffle"
						:for="randomizeCheckboxId"
						title="Randomize"
					/>

					<!-- delete button -->
					<button
						class="btn btn-outline-secondary"
						@click="handleRemove"
						title="Delete"
					>
						<i class="bi bi-trash-fill" />
					</button>
				</div>

				<a
					class="link-secondary"
					:href="sitemap"
					target="_blank"
					rel="noopener noreferrer"
					>{{ sitemap }}</a
				>
			</div>
		</td>

		<td class="text-center align-middle">
			{{ stats.totalUrls }}
		</td>

		<td class="text-center align-middle">
			{{ stats.count }}
		</td>

		<td
			v-for="status in seenStatuses"
			class="text-center align-middle"
			:key="status"
		>
			{{ stats.statusesCount[status] }}
		</td>

		<td class="position-relative text-center p-0">
			<!-- placeholder for progress -->
			<div class="h-100 center">
				{{ (stats.progress * 100).toFixed(2) }}%
			</div>

			<!-- clip-path with white progress text and a progress bar -->
			<div
				class="h-100 center"
				:style="{
					'clip-path':
						'inset(0% ' + (1 - stats.progress) * 100 + '% 0% 0%)',
					transition: 'clip-path 0.6s ease',
					color: '#eee',
				}"
			>
				{{ (stats.progress * 100).toFixed(2) }}%
				<div
					:class="[
						'center',
						'progress-bar',
						'bg-success',
						'progress-bar-striped',
						{ 'progress-bar-animated': running },
					]"
					role="progressbar"
				/>
			</div>
		</td>
	</tr>
</template>

<script lang="ts">
import { useUUID } from "@/hooks/useUUID";
import { watcherService } from "@/services/watcherService";
import { getPathname } from "@/utils";
import { computed, defineComponent, toRefs } from "vue";

export default defineComponent({
	props: {
		sitemap: {
			type: String,
			required: true,
		},
	},
	setup(props) {
		const { sitemap } = toRefs(props);

		const {
			sitemapStats,
			removeSitemap,
			continueWatching,
			pauseWatching,
			seenStatuses,
			setSitemapVisibility,
			setSitemapRandomize,
		} = watcherService;
		const stats = computed(() => sitemapStats.value[sitemap.value]);

		const handleRemove = () => removeSitemap(sitemap.value);

		const running = computed(
			() => sitemapStats.value[sitemap.value].running
		);
		const toggleStatus = () => {
			if (running.value) {
				pauseWatching(sitemap.value);
			} else {
				continueWatching(sitemap.value);
			}
		};

		const visible = computed(() => stats.value.show);
		const toggleVisibility = () => {
			setSitemapVisibility(sitemap.value, !visible.value);
		};

		const randomize = computed(() => stats.value.randomize);
		const toggleRandomizeSitemap = () => {
			setSitemapRandomize(sitemap.value, !randomize.value);
		};

		const componentUUID = useUUID();
		const randomizeCheckboxId = `randomize-checkbox-${componentUUID.value}`;

		return {
			pathname: getPathname(sitemap.value),
			stats,
			seenStatuses,

			handleRemove,

			running,
			toggleStatus,

			visible,
			toggleVisibility,

			randomize,
			toggleRandomizeSitemap,

			componentUUID,
			randomizeCheckboxId,
		};
	},
});
</script>

<style scoped>
.drag-handle {
	cursor: move;
}

.center {
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
}

.progress-bar {
	z-index: -1;
}
</style>
